import { useState } from 'react';
import PropTypes from 'prop-types';

import InputField from '@components/inputs/input';
import CombinedInputField from '@components/inputs/combined-input';
import Select from '@components/select';
import Link from '@components/link';
import Button from '@components/button';
import Toast from '@components/toast';
import CalculatorLayout from '../shared/calculator-layout';
import PaymentChart from '../shared/payment-chart';

import { calculatePaymentBreakdown } from '../shared/calculator-utils';
import useCalculatorInputs from '../shared/use-calculator-inputs';
import useInterestRate from '../shared/use-interest-rate';
import useLoanTerm from '../shared/use-loan-term';
import {
  LOAN_TERM_OPTIONS,
  CTA_BUTTON_CONFIG,
  DISCLAIMER_URLS
} from '../shared/calculator-ui-constants';

import styles from './styles.module.scss';
const { calculator_cta, results_header, cta_wrapper, select_wrapper } = styles;

const MortgageCalculatorEnhanced = ({
  price,
  currentRates,
  lastUpdatedOn,
  calculatorOptions = [],
  onCalculatorChange,
  showCalculatorSelector = true,
  hideVisual = false,
  condensed = false,
  layout = 'balanced',
  gridLayout = false,
  title,
  description
}) => {
  // Use custom hooks for calculator state management
  const sharedInputs = useCalculatorInputs(price || 500000, 20);
  const {
    homePrice,
    downPayment,
    downPaymentPercent,
    loanAmount,
    handleHomePriceChange,
    handleDownPaymentChange: sharedHandleDownPaymentChange,
    handleDownPaymentPercentChange: sharedHandleDownPaymentPercentChange
  } = sharedInputs;

  // Allow unclamped values during typing for real-time feedback
  const handleDownPaymentChange = (value) => {
    sharedHandleDownPaymentChange(value);
  };

  const handleDownPaymentPercentChange = (value) => {
    sharedHandleDownPaymentPercentChange(value);
  };

  // Clamp to 5% minimum on blur
  const handleDownPaymentBlur = () => {
    const minDownPayment = homePrice * 0.05;
    if (downPayment < minDownPayment) {
      sharedHandleDownPaymentChange(minDownPayment);
      Toast.notify({
        message: 'Conventional loans require a minimum 5% down payment',
        type: 'info'
      });
    }
  };

  const handleDownPaymentPercentBlur = () => {
    if (downPaymentPercent < 5) {
      sharedHandleDownPaymentPercentChange(5);
      Toast.notify({
        message: 'Conventional loans require a minimum 5% down payment',
        type: 'info'
      });
    } else if (downPaymentPercent > 100) {
      sharedHandleDownPaymentPercentChange(100);
    }
  };

  const { interestRate, handleInterestRateChange, setInterestRate } = useInterestRate(
    currentRates?.[30] || 6.42
  );

  const { loanTerm, handleLoanTermChange } = useLoanTerm(30, currentRates, setInterestRate);

  const [monthlyTaxes, setMonthlyTaxes] = useState(0);
  const [monthlyInsurance, setMonthlyInsurance] = useState(0);

  const paymentBreakdown = calculatePaymentBreakdown(
    loanAmount,
    interestRate,
    loanTerm,
    homePrice,
    monthlyTaxes,
    monthlyInsurance
  );

  const chartData = [
    { name: 'Principal & Interest', value: paymentBreakdown.principalAndInterest },
    { name: 'Taxes', value: paymentBreakdown.taxes },
    { name: 'Insurance', value: paymentBreakdown.insurance },
    { name: 'Mortgage Insurance', value: paymentBreakdown.mortgageInsurance }
  ];

  const disclaimerContent = (
    <>
      *The rates & payments shown are illustrative only.
      <br />
      Payment displayed includes principal, interest, taxes, and insurance. Rates last updated on{' '}
      {lastUpdatedOn ? new Date(lastUpdatedOn).toLocaleDateString() : 'N/A'} from Freddie Mac
      Primary Mortgage Market Survey.{' '}
      <Link url={DISCLAIMER_URLS.findLoanOfficer}>Contact a loan officer</Link> for actual
      rate/payment quotes.
    </>
  );

  const resultsContent = (
    <>
      <h3 className={results_header}>Your estimated payment</h3>
      <PaymentChart
        data={chartData}
        centerLabel="per month"
        condensed={condensed}
        hideVisual={hideVisual}
      />
      <div className={cta_wrapper}>
        <Button
          type="button"
          label={CTA_BUTTON_CONFIG.label}
          url={CTA_BUTTON_CONFIG.url}
          theme={CTA_BUTTON_CONFIG.theme}
          className={calculator_cta}
        />
      </div>
    </>
  );

  return (
    <CalculatorLayout
      title={title || 'Mortgage Calculator'}
      description={description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit'}
      calculatorType="mortgage"
      calculatorOptions={calculatorOptions}
      onCalculatorChange={onCalculatorChange}
      showCalculatorSelector={showCalculatorSelector}
      hideVisual={hideVisual}
      condensed={condensed}
      layout={layout}
      gridLayout={gridLayout}
      resultsContent={resultsContent}
      disclaimerContent={disclaimerContent}
    >
      <InputField
        label="Home Price"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={homePrice.toString()}
        defaultOnChange={handleHomePriceChange}
        inputName="homePrice"
      />

      <CombinedInputField
        label="Down Payment"
        leftInputName="downPayment"
        rightInputName="downPaymentPercent"
        defaultLeftValue={downPayment}
        defaultRightValue={downPaymentPercent}
        onLeftChange={handleDownPaymentChange}
        onRightChange={handleDownPaymentPercentChange}
        onLeftBlur={handleDownPaymentBlur}
        onRightBlur={handleDownPaymentPercentBlur}
      />

      <InputField
        label="Interest Rate"
        inputType="text"
        inputFormat="percentage"
        defaultInputValue={interestRate.toString()}
        defaultOnChange={handleInterestRateChange}
        inputName="interestRate"
      />

      <div className={select_wrapper}>
        <Select
          id="loanTerm"
          label="Loan Term"
          options={LOAN_TERM_OPTIONS}
          selected={loanTerm}
          onChange={handleLoanTermChange}
        />
      </div>

      <InputField
        label="Monthly Taxes & Insurance (optional)"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={'0'}
        defaultOnChange={(value) => {
          const total = parseFloat(value) || 0;
          setMonthlyTaxes(total * 0.6);
          setMonthlyInsurance(total * 0.4);
        }}
        inputName="monthlyTaxesInsurance"
      />
    </CalculatorLayout>
  );
};

MortgageCalculatorEnhanced.propTypes = {
  price: PropTypes.number,
  currentRates: PropTypes.shape({
    15: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    30: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  lastUpdatedOn: PropTypes.string,
  calculatorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onCalculatorChange: PropTypes.func,
  showCalculatorSelector: PropTypes.bool,
  hideVisual: PropTypes.bool,
  condensed: PropTypes.bool,
  layout: PropTypes.oneOf(['balanced', 'results-focused', 'inputs-focused']),
  gridLayout: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string
};

export default MortgageCalculatorEnhanced;
